import isMobile from 'is-mobile';
import srraf from 'srraf';
import { section } from '@/neon';
import Flickity from 'flickity';
import { on } from '@/neon/events';
import { qs, qsa, toggle, listen, add, remove } from '@fluorescent/dom';

import productForm from '@/components/productForm';
import { optionButtons, quantityInput, switchImage } from '@/lib/product';
import reviews from '@/components/reviews';
import accordion from '@/components/accordion';
import variantPopup from '@/components/variantPopup';
import Media from '@/lib/media';
import Lightbox from '@/lib/lightbox';
import heightObserver from '@/lib/heightObserver';
import { backgroundVideoHandler } from '@/lib/a11y';

const selectors = {
  sectionWrapper: '.product-page-wrapper',
  imagesContainer: '.product__media',
  images: '.media__image',
  swatches: '[data-product-swatches]',
  chips: '[data-product-chips]',
  quantityInput: '.product-form__quantity',
  socialSharing: '.share',
  productDetailsWrapper: '.product__meta-inner',
  productDescription: '.product__description',
  accordion: '.accordion',
  productForm: '[data-product-form]',
  lightboxTrigger: '.product__media-action-button',
  thumb: '[data-product-thumbnail]',
  inYourSpace: '[data-in-your-space]',
};

const carouselOpts = {
  adaptiveHeight: false,
  arrowShape:
    'M27.64 51.03 42.52 35.13 45.43 37.89 34.83 49.09 71.63 49.09 71.63 53.09 34.83 53.09 45.25 64.12 42.52 67.05 27.64 51.03z',
  pageDots: false,
  watchCSS: true,
};

// LERP returns a number between start and end based on the amt
// Often used to smooth animations
// Eg. Given: start = 0, end = 100
// - if amt = 0.1 then lerp will return 10
// - if amt = 0.5 then lerp will return 50
// - if amt = 0.9 then lerp will return 90
function lerp(start, end, amt) {
  return (1 - amt) * start + amt * end;
}

section('product', {
  onLoad() {
    this.container.classList.add('product--loaded');
    this.media = Media(qs('.product__media-container', this.container));
    const productFormContainer = this.container.querySelector(
      selectors.productForm
    );

    const inYourSpaceButton = this.container.querySelector(
      '.product__view-in-space'
    );

    if (isMobile() && Boolean(inYourSpaceButton)) {
      inYourSpaceButton.classList.add('visible');
    }

    this.productForm = productForm(productFormContainer, {
      isProductPage: true,
      container: this.container,
    });

    this.optionButtons = optionButtons(
      qsa('[data-option-buttons]', this.container)
    );
    this.quantityInput = quantityInput.call(this, selectors.quantityInput);
    this.sectionWrapper = document.querySelector(selectors.sectionWrapper);
    this.productDetails = this.container.querySelector(
      selectors.productDetailsWrapper
    );
    this.variantPopup = variantPopup(this.container);

    // Here we check if the merchant added a product reviews section or not
    const reviewsContainer = document.querySelector('#shopify-product-reviews');
    const reviewsTemplate = this.container.querySelector(
      '.product-reviews-template'
    );
    const reviewsDrawer = this.container.querySelector('[data-reviews-drawer]');
    let reviewsIsDrawer = false;

    if (!reviewsContainer && reviewsTemplate && reviewsDrawer) {
      reviewsDrawer.classList.add('reviews-flyout--active');
      reviewsDrawer.appendChild(reviewsTemplate.content.cloneNode(true));
      reviewsIsDrawer = true;
    }

    window.SPRCallbacks = {};

    window.SPRCallbacks.onReviewsLoad = () => {
      if (!this.reviews) {
        this.reviews = reviews(this.container, reviewsIsDrawer);
      }
    };

    this.accordions = this.container.querySelectorAll(selectors.accordion);
    this.accordions.forEach(item => {
      if (!item.classList.contains('accordion--active')) {
        accordion(item);
      }
    });

    this.socialButtons = qsa('[data-social-share]', this.container);

    this.socialButtonsClick = listen(this.socialButtons, 'click', e => {
      toggle(e.target, 'active');
      const sub = qs('.product__share-icons', e.target);

      sub.setAttribute('aria-hidden', !e.target.classList.contains('active'));
    });

    this._initLightbox();
    this._initThumbnails();
    this._initSlider();
    this._wrapTables();

    this.mediaUpateListener = on('product:mediaSelect', ({ selectedMedia }) =>
      this.carousel.select(selectedMedia)
    );

    document.body.classList.remove('product--full-width');

    if (this.container.classList.contains('product--full-width')) {
      document.body.classList.add('product--full-width');
    }

    const featureWidgetVideo = qs(
      '.product-feature-widget--video',
      this.container
    );

    if (featureWidgetVideo) {
      this.videoHandler = backgroundVideoHandler(this.container);
    }

    this.container.style.setProperty('--product-details-top', 0);
    if (!isMobile()) {
      this._initDetailsScroll();
    }
  },

  _initDetailsScroll() {
    // The previous scroll position of the page
    this.previousScrollY = window.scrollY;
    // To keep track of the amount scrolled per event
    this.currentScrollAmount = 0;
    // Height of the header bar, used for calculating position
    this.headerHeight = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue('--header-height')
        .replace(/px/gi, '')
    );
    // The value to set the product details `top` value to
    this.detailsTop = this.headerHeight;
    this.detailsTopPrevious = this.detailsTop;
    // The height of the product details container
    // Gets updated by a resize observer on the window and the details container
    this.detailsHeight = this.productDetails.offsetHeight;
    // The height of the product details container plus the height of the header
    this.detailsHeightWithHeader = this.detailsHeight + this.headerHeight;
    // The max amount to set the details `top` value
    // This is equal to the number of pixels that the details container is hidden by the viewport
    // Gets updated by a resize observer on the window and the details container
    this.detailsMaxTop = this.detailsHeightWithHeader - window.innerHeight;

    this.container.style.setProperty(
      '--product-details-top',
      `${this.detailsTop}px`
    );

    // Whatch scroll updates
    this.scroller = srraf(({ y }) => {
      this._scrollHandler(y);
    });

    // Resize observer on the window and the product details
    // Things like accordions can change the height of the details container
    this.resizeObserver = heightObserver(() => {
      this.detailsHeight = this.productDetails.offsetHeight;
      this.headerHeight = parseInt(
        getComputedStyle(document.documentElement)
          .getPropertyValue('--header-height')
          .replace(/px/gi, '')
      );
      this.detailsHeightWithHeader = this.detailsHeight + this.headerHeight;
      this.detailsMaxTop = this.detailsHeightWithHeader - window.innerHeight;

      // Check if the product details container is taller than the viewport
      // and section container has room for the details to scroll.
      // If thumbnails are shown then the details are probably the tallest
      // element in the section and won't have room to scroll.
      if (
        this.detailsHeightWithHeader > window.innerHeight &&
        this.container.offsetHeight > this.detailsHeightWithHeader
      ) {
        add(this.container, 'product--has-sticky-scroll');
        this._scrollHandler(window.scrollY);
      } else {
        remove(this.container, 'product--has-sticky-scroll');
      }
    });

    this.resizeObserver.observe(this.productDetails);
    this.resizeObserver.observe(document.documentElement);

    // Start the animation loop
    requestAnimationFrame(() => this.updateDetailsTopLoop());
  },

  // This is an endless RAF loop used to update the top position CSS var
  // We're using this with a LERP function to smooth out the position updating
  // instead of having large jumps while scrolling fast
  updateDetailsTopLoop() {
    // We want to continue to update the top var until fully into the stopped position
    if (this.detailsTop !== this.detailsTopPrevious) {
      this.detailsTopPrevious = lerp(
        this.detailsTopPrevious,
        this.detailsTop,
        0.5
      );
      this.container.style.setProperty(
        '--product-details-top',
        `${this.detailsTopPrevious}px`
      );
    }
    requestAnimationFrame(() => this.updateDetailsTopLoop());
  },

  _scrollHandler(y) {
    // this.detailsTopPrevious = this.detailsTop;
    this.currentScrollAmount = this.previousScrollY - y;

    // The offset based on how far the page has been scrolled from last event
    const currentScrollOffset = this.detailsTop + this.currentScrollAmount;
    // The max top value while scrolling up
    const topMax = this.headerHeight;
    // The max top value while scrolling down
    const bottomMax = -this.detailsMaxTop + this.headerHeight - 40;

    // Calculate the current top value based on the currentScrollOffset value in the range of topMax and bottomMax
    this.detailsTop = Math.max(
      bottomMax,
      Math.min(currentScrollOffset, topMax)
    );

    // Update the previous scroll position for next time
    this.previousScrollY = y;
  },

  _initThumbnails() {
    this.productThumbs = this.container.querySelectorAll(selectors.thumb);

    this.productThumbs.forEach(thumb => {
      thumb.addEventListener('click', this._handleThumbClick.bind(this));
    });
  },

  _handleThumbClick(e) {
    e.preventDefault();
    const {
      currentTarget: { dataset },
    } = e;

    this.productThumbs.forEach(thumb => thumb.classList.remove('active'));
    e.currentTarget.classList.add('active');

    switchImage(this.container, dataset.thumbnailId);
  },

  _initLightbox() {
    this.images = this.container.querySelectorAll(selectors.images);
    this.lightboxTrigger = this.container.querySelector(
      selectors.lightboxTrigger
    );

    const lightbox = this.container.querySelector('[data-lightbox]');

    this.lightbox = Lightbox(lightbox);

    this.images.forEach(image => {
      image.addEventListener('click', this._handleImageClick.bind(this));
    });
    this.lightboxTrigger &&
      this.lightboxTrigger.addEventListener('click', this.lightbox.open);
  },

  _handleImageClick(e) {
    e.preventDefault();
    this.lightbox.open(e.currentTarget.dataset.open);
    this.media && this.media.pauseActiveMedia();
  },

  _initSlider() {
    const imagesContainer = this.container.querySelector(
      selectors.imagesContainer
    );

    const images = imagesContainer.querySelectorAll('.media-wrapper');

    carouselOpts.wrapAround = images.length > 2;

    this.carousel = new Flickity(imagesContainer, {
      ...carouselOpts,
      on: {
        ready: function () {
          if (this.selectedElement.querySelector('[data-media-type="model"]')) {
            this.unbindDrag();
          }
        },
        change: () => {
          // Disable dragging so user can interact with model
          if (
            this.carousel.selectedElement.querySelector(
              '[data-media-type="model"]'
            )
          ) {
            this.carousel.unbindDrag();

            const newImageMedia = qs('.media', this.carousel.selectedElement);
            const inYourSpaceButton = qs(selectors.inYourSpace, this.container);

            if (inYourSpaceButton) {
              if (newImageMedia.dataset.mediaType === 'model') {
                inYourSpaceButton.setAttribute(
                  'data-shopify-model3d-id',
                  newImageMedia.dataset.mediaId
                );
              }
            }
          } else {
            this.carousel.bindDrag();
          }
        },
      },
    });
  },

  _wrapTables() {
    const tables = this.container.querySelectorAll('table');

    tables.forEach(el => {
      const wrapper = document.createElement('div');
      wrapper.classList.add('rte-table');

      el.parentNode.insertBefore(wrapper, el);

      wrapper.appendChild(el);
    });
  },

  onUnload() {
    this.lightbox.destroy();
    this.reviews && this.reviews.unload();
    this.quantityInput && this.quantityInput();
    this.productForm.unload();
    this.carousel.destroy();
    this.mediaUpateListener();
    this.variantPopup.unload();
    this.optionButtons.destroy();
    this.socialButtonsClick();
    this.resizeObserver?.unload();
    this.scroller?.scroller.destroy();

    this.images.forEach(image => {
      image.removeEventListener('click', this._handleImageClick);
    });

    this.lightboxTrigger &&
      this.lightboxTrigger.removeEventListener('click', this.lightbox.open);
  },
});
